import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown, Form } from 'react-bootstrap';

import { UseOutletContextDocument } from './Document';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { assignmentEntityLabel, assignmentEntityUrl, assignmentDisplayReference } from '../../../scripts/document.assignment.script';
import { MediaDA } from '../../../services/admin/media.service';
import { Error } from '../../../models/error.model';
import { DocumentInbox, DocumentInboxData } from '../../../models/document.inbox.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { colorStatusDocumentInbox } from '../../../scripts/badge.color.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { getError } from '../../../languages/translations/response';
import { sortColumn } from '../../../scripts/table.script';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { statusDocumentInboxList, statusDocumentInboxValue } from '../../../libraries/status.document.inbox.library';
import { modalShow } from '../../../utils/modal.util';
import { isCopilotAllowed } from '../../../scripts/copilot.auth';
import { openCopilot } from '../../../scripts/copilot.controller';
import { usePdfViewer } from '../../../contexts/PdfViewerContext';
import { usePaginationUrl } from '../../../hooks/usePaginationUrl';
import { useDebounce } from '../../../hooks/useDebounce';
import { onDocumentAssigned } from '../../../scripts/document.controller';

import AppSortTable from '../../../components/element/SortTable';
import AppPagination from '../../../components/element/Pagination';
import AppPreloader from '../../../components/Preloader';
import AppModalDocumentUpload from '../../../components/modal/ModalDocumentUpload';
import AppStorageRemove from '../../../components/element/StorageRemove';
import DropdownActions from '../../../components/dropdown/DropdownActions';

export interface DocumentListPageProps {};

let errorResponse: Error, documentsResponse: DocumentInboxData;

const DocumentListPage: React.FunctionComponent<DocumentListPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextDocument()
  const {lang} = StateLanguage()
  const {open: openPdfViewer} = usePdfViewer()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { page: urlPage, setPage: setUrlPage, clampPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({status: '', sort_field: '', sort_mode: '', dated_from: '', dated_to: '', uploaded_from: '', uploaded_to: ''})
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [documents, setDocuments] = useState<DocumentInbox[] | null>(null)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchInput, 400)
  const [activeSearch, setActiveSearch] = useState(searchParams.get('search') ?? '')
  const [lifecycleView, setLifecycleView] = useState<'active' | 'ignored' | 'archived'>(
    (searchParams.get('lifecycle_view') as 'active' | 'ignored' | 'archived') || 'active'
  )

  const formatSize = (bytes: number): string => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const hasActiveFilters = () => {
    return filter.status.length > 0 || filter.dated_from.length > 0 || filter.dated_to.length > 0 || filter.uploaded_from.length > 0 || filter.uploaded_to.length > 0
  }

  const handleChangeFilterStatus = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter({...filter, status: event.target.value})
  }

  const handleChangeFilterDatedFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, dated_from: event.target.value})
  }

  const handleChangeFilterDatedTo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, dated_to: event.target.value})
  }

  const handleChangeFilterUploadedFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, uploaded_from: event.target.value})
  }

  const handleChangeFilterUploadedTo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, uploaded_to: event.target.value})
  }

  const executeFilterApply = () => {
    if (companyForUser && hasActiveFilters()) {
      filter.sort_field = ''
      filter.sort_mode = ''
      setUrlPage(1)
      loadDocuments(companyForUser.id, 1, activeSearch)
    }
  }

  const executeFilterReset = () => {
    if (companyForUser && hasActiveFilters()) {
      filter.status = ''
      filter.dated_from = ''
      filter.dated_to = ''
      filter.uploaded_from = ''
      filter.uploaded_to = ''
      filter.sort_field = ''
      filter.sort_mode = ''
      localStorage.removeItem('filter_documents')
      setUrlPage(1)
      loadDocuments(companyForUser.id, 1, activeSearch)
    }
  }

  const executeFilterRemove = (item: string) => {
    if (companyForUser && hasActiveFilters()) {
      for (let key of Object.keys(filter) as Array<keyof typeof filter>) {
        if (key === item) {
          filter[key] = ''
        }
      }
      loadDocuments(companyForUser.id, 1, activeSearch)
    }
  }

  const loadDocuments = async (id_company: string, page: number, search?: string, lifecycle?: string) => {
    const df = filter.dated_from && filter.dated_to && filter.dated_from > filter.dated_to ? filter.dated_to : filter.dated_from
    const dt = filter.dated_from && filter.dated_to && filter.dated_from > filter.dated_to ? filter.dated_from : filter.dated_to
    const uf = filter.uploaded_from && filter.uploaded_to && filter.uploaded_from > filter.uploaded_to ? filter.uploaded_to : filter.uploaded_from
    const ut = filter.uploaded_from && filter.uploaded_to && filter.uploaded_from > filter.uploaded_to ? filter.uploaded_from : filter.uploaded_to

    const effectiveLifecycle = lifecycle ?? lifecycleView
    await MediaDA.getDocuments(id_company, filter.status, filter.sort_field, filter.sort_mode, page, pagination.limit, search, df, dt, uf, ut, undefined, undefined, effectiveLifecycle).then( (response) => {
      if (response.status === 200) {
        documentsResponse = response.data

        localStorage.setItem('filter_documents', JSON.stringify({
          company_id: id_company,
          status: filter.status,
          sort_field: filter.sort_field,
          sort_mode: filter.sort_mode,
          dated_from: filter.dated_from,
          dated_to: filter.dated_to,
          uploaded_from: filter.uploaded_from,
          uploaded_to: filter.uploaded_to,
          page,
          search: search ?? '',
          lifecycle_view: effectiveLifecycle
        }))

        const totalPages = documentsResponse.pagination.pages || 1

        if (page > totalPages && totalPages >= 1) {
          setUrlPage(totalPages)
          loadDocuments(id_company, totalPages)
          return
        }

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= totalPages; i++) {
          pagesOfPagination.push(i)
        }

        setDocuments(documentsResponse.items)
        setPagination({...pagination, current: documentsResponse.pagination.page, total: documentsResponse.pagination.total, pages: pagesOfPagination})
        setUrlPage(documentsResponse.pagination.page)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const executeSortList = (item: string) => {
    if (!companyForUser) return null

    sortColumn(item, filter)
    setUrlPage(1)
    loadDocuments(companyForUser.id, 1, activeSearch)
  }

  const submitDeleteDocument = (itemId: string) => {
    if (!companyForUser) return
    GlobalSwal.fire({
      title: lang.labels.deleteDocument,
      text: lang.labels.youWantToDeleteThisDocument,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await MediaDA.deleteDocument(companyForUser.id, itemId).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.successfullyDeletedDocument,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadDocuments(companyForUser.id, urlPage, activeSearch)
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const submitIgnoreDocument = (itemId: string) => {
    if (!companyForUser) return
    GlobalSwal.fire({
      title: lang.labels.ignoreDocument,
      text: lang.labels.youWantToIgnoreThisDocument,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await MediaDA.ignoreDocument(companyForUser.id, itemId).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.successfullyIgnored,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadDocuments(companyForUser.id, urlPage, activeSearch)
            })
          } else if (response.status === 409) {
            GlobalSwal.fire({
              title: lang.labels.cannotIgnoreDocument,
              text: lang.labels.cannotIgnoreDocumentWithAssignments,
              icon: 'warning',
              confirmButtonText: lang.labels.okGotIt
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const submitArchiveDocument = (itemId: string) => {
    if (!companyForUser) return
    GlobalSwal.fire({
      title: lang.labels.archiveDocument,
      text: lang.labels.youWantToArchiveThisDocument,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await MediaDA.archiveDocument(companyForUser.id, itemId).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.successfullyArchived,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadDocuments(companyForUser.id, urlPage, activeSearch)
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const submitUnignoreDocument = (itemId: string) => {
    if (!companyForUser) return
    GlobalSwal.fire({
      title: lang.labels.unignoreDocument,
      text: lang.labels.youWantToUnignoreThisDocument,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await MediaDA.unignoreDocument(companyForUser.id, itemId).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.successfullyUnignored,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadDocuments(companyForUser.id, urlPage, activeSearch)
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const submitUnarchiveDocument = (itemId: string) => {
    if (!companyForUser) return
    GlobalSwal.fire({
      title: lang.labels.unarchiveDocument,
      text: lang.labels.youWantToUnarchiveThisDocument,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await MediaDA.unarchiveDocument(companyForUser.id, itemId).then( (response) => {
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.successfullyUnarchived,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              loadDocuments(companyForUser.id, urlPage, activeSearch)
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleUploadSuccess = () => {
    if (companyForUser) {
      setUrlPage(1)
      loadDocuments(companyForUser.id, 1, activeSearch)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadDocuments(companyForUser.id, current, activeSearch)
  }

  useEffect( () => {
    let filter_documents: any = null
    try { filter_documents = JSON.parse(localStorage.getItem('filter_documents') ?? 'null') } catch { /* corrupted session — use defaults */ }

    if (companyForUser) {
      const isMatch = filter_documents && filter_documents.company_id === companyForUser.id
      filter.status = isMatch ? filter_documents.status ?? '' : ''
      filter.sort_field = isMatch ? filter_documents.sort_field ?? '' : ''
      filter.sort_mode = isMatch ? filter_documents.sort_mode ?? '' : ''
      filter.dated_from = isMatch ? filter_documents.dated_from ?? '' : ''
      filter.dated_to = isMatch ? filter_documents.dated_to ?? '' : ''
      filter.uploaded_from = isMatch ? filter_documents.uploaded_from ?? '' : ''
      filter.uploaded_to = isMatch ? filter_documents.uploaded_to ?? '' : ''
      pagination.total = 0

      const restoredSearch = isMatch ? filter_documents.search ?? '' : ''
      const restoredPage = isMatch ? Math.max(1, Number(filter_documents.page) || 1) : 1
      const restoredLifecycle: 'active' | 'ignored' | 'archived' = isMatch && ['active', 'ignored', 'archived'].includes(filter_documents.lifecycle_view) ? filter_documents.lifecycle_view : 'active'

      setSearchInput(restoredSearch)
      setActiveSearch(restoredSearch)
      setLifecycleView(restoredLifecycle)
      setUrlPage(restoredPage)
      setDocuments(null)
      loadDocuments(companyForUser.id, restoredPage, restoredSearch, restoredLifecycle)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.documents, branch: lang.labels.listDocuments}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect( () => {
    if (mounted && searchParams.get('upload') === '1') {
      modalShow('modal-document-upload')
      searchParams.delete('upload')
      setSearchParams(searchParams, { replace: true })
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, searchParams])

  useEffect(() => {
    if (debouncedSearch !== activeSearch) {
      setActiveSearch(debouncedSearch)
      setUrlPage(1)

      const newParams = new URLSearchParams(searchParams)
      if (debouncedSearch) {
        newParams.set('search', debouncedSearch)
      } else {
        newParams.delete('search')
      }
      newParams.set('page', '1')
      setSearchParams(newParams, { replace: true })

      if (companyForUser) {
        loadDocuments(companyForUser.id, 1, debouncedSearch)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return onDocumentAssigned(() => {
      if (companyForUser) {
        loadDocuments(companyForUser.id, urlPage, activeSearch)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, urlPage, activeSearch])

  const handleLifecycleChange = (view: 'active' | 'ignored' | 'archived') => {
    setLifecycleView(view)
    const newParams = new URLSearchParams(searchParams)
    if (view === 'active') {
      newParams.delete('lifecycle_view')
    } else {
      newParams.set('lifecycle_view', view)
    }
    newParams.set('page', '1')
    setSearchParams(newParams, { replace: true })
    setUrlPage(1)
    setDocuments(null)
    if (companyForUser) {
      // Need to call with the new view directly since state hasn't updated yet
      const df = filter.dated_from && filter.dated_to && filter.dated_from > filter.dated_to ? filter.dated_to : filter.dated_from
      const dt = filter.dated_from && filter.dated_to && filter.dated_from > filter.dated_to ? filter.dated_from : filter.dated_to
      const uf = filter.uploaded_from && filter.uploaded_to && filter.uploaded_from > filter.uploaded_to ? filter.uploaded_to : filter.uploaded_from
      const ut = filter.uploaded_from && filter.uploaded_to && filter.uploaded_from > filter.uploaded_to ? filter.uploaded_from : filter.uploaded_to
      MediaDA.getDocuments(companyForUser.id, filter.status, filter.sort_field, filter.sort_mode, 1, pagination.limit, activeSearch, df, dt, uf, ut, undefined, undefined, view).then((response) => {
        if (response.status === 200) {
          documentsResponse = response.data
          const totalPages = documentsResponse.pagination.pages || 1
          let pagesOfPagination: number[] = []
          for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
          setDocuments(documentsResponse.items)
          setPagination({...pagination, current: documentsResponse.pagination.page, total: documentsResponse.pagination.total, pages: pagesOfPagination})
          setUrlPage(documentsResponse.pagination.page)
          localStorage.setItem('filter_documents', JSON.stringify({
            company_id: companyForUser.id,
            status: filter.status,
            sort_field: filter.sort_field,
            sort_mode: filter.sort_mode,
            dated_from: filter.dated_from,
            dated_to: filter.dated_to,
            uploaded_from: filter.uploaded_from,
            uploaded_to: filter.uploaded_to,
            page: 1,
            search: activeSearch,
            lifecycle_view: view
          }))
        }
      })
    }
  }

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && documents
        ?
        <div className="card card-flush">
          <div className="card-header border-0 pt-5">
            <div className="card-title">{lang.labels.listDocuments}</div>
            <div className="card-toolbar">
              <Dropdown>
                <Dropdown.Toggle variant={`btn ${hasActiveFilters() ? "btn-primary" : "btn-light-primary"} mb-1 me-2`}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.filter}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                  <div className="mt-5 mx-5">
                    <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                  </div>
                  <div className="separator border-gray-200"></div>
                  <div className="mt-3 m-5">
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.status}:</label>
                      <Form.Select bsPrefix="form-select form-select-sm" name="status" value={filter.status} onChange={handleChangeFilterStatus}>
                        <option value="" className="d-none">{lang.labels.select}</option>
                        { statusDocumentInboxList(lang).map(( (item, index) => (
                          <option key={index} value={item.code}>{item.display}</option>
                        )))}
                      </Form.Select>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-6 mb-2 mb-md-0">
                        <label className="form-label fs-7">{lang.labels.filterDocumentDateFrom}:</label>
                        <input className="form-control form-control-sm" type="date" name="dated-from" max={filter.dated_to || undefined} value={filter.dated_from} onChange={handleChangeFilterDatedFrom} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fs-7">{lang.labels.filterDocumentDateTo}:</label>
                        <input className="form-control form-control-sm" type="date" name="dated-to" min={filter.dated_from || undefined} value={filter.dated_to} onChange={handleChangeFilterDatedTo} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-6 mb-2 mb-md-0">
                        <label className="form-label fs-7">{lang.labels.filterUploadDateFrom}:</label>
                        <input className="form-control form-control-sm" type="date" name="uploaded-from" max={filter.uploaded_to || undefined} value={filter.uploaded_from} onChange={handleChangeFilterUploadedFrom} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fs-7">{lang.labels.filterUploadDateTo}:</label>
                        <input className="form-control form-control-sm" type="date" name="uploaded-to" min={filter.uploaded_from || undefined} value={filter.uploaded_to} onChange={handleChangeFilterUploadedTo} />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-5">
                      <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                      <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <button className="btn btn-primary mb-1 me-0" type="button" onClick={() => modalShow('modal-document-upload')}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.uploadDocument}
                </div>
              </button>
            </div>
          </div>
          <div className="d-flex align-items-center mx-7 mb-4">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${lifecycleView === 'active' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => handleLifecycleChange('active')}
              >
                {lang.labels.inbox}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${lifecycleView === 'ignored' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => handleLifecycleChange('ignored')}
              >
                {lang.labels.ignored}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${lifecycleView === 'archived' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => handleLifecycleChange('archived')}
              >
                {lang.labels.archives}
              </button>
            </div>
          </div>
          { hasActiveFilters() &&
            <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${filter.status.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("status")}>
                <AppStorageRemove attribute={lang.labels.status} description={statusDocumentInboxValue(lang, filter.status)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${filter.dated_from.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("dated_from")}>
                <AppStorageRemove attribute={lang.labels.filterDocumentDateFrom} description={filter.dated_from}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${filter.dated_to.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("dated_to")}>
                <AppStorageRemove attribute={lang.labels.filterDocumentDateTo} description={filter.dated_to}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${filter.uploaded_from.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("uploaded_from")}>
                <AppStorageRemove attribute={lang.labels.filterUploadDateFrom} description={filter.uploaded_from}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${filter.uploaded_to.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("uploaded_to")}>
                <AppStorageRemove attribute={lang.labels.filterUploadDateTo} description={filter.uploaded_to}></AppStorageRemove>
              </button>
            </div>
          }
          <div className="card-body pt-0">
            <div className="mb-5">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder={lang.labels.searchDocumentsPlaceholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value.slice(0, 200))}
                  maxLength={200}
                  aria-label={lang.labels.searchDocuments}
                />
                {searchInput.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary border-start-0 bg-white"
                    onClick={() => setSearchInput('')}
                    title={lang.labels.clear}
                    aria-label={lang.labels.clear}
                  >
                    <i className="bi bi-x fs-4"></i>
                  </button>
                )}
              </div>
              {activeSearch.length > 0 && (
                <div className="form-text mt-1">
                  {documents === null
                    ? <span><i className="bi bi-arrow-repeat fs-7 me-1"></i>{lang.labels.searchingDocuments}</span>
                    : pagination.total === 0
                      ? <span className="text-muted">{lang.labels.searchDocumentsNoResults.replace('{{query}}', activeSearch)}</span>
                      : <span className="text-muted">{lang.labels.searchDocumentsResultsCount.replace('{{count}}', String(pagination.total)).replace('{{query}}', activeSearch)}</span>
                  }
                </div>
              )}
            </div>
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("name")}>
                          <span className="me-1">{lang.labels.name}</span>
                          <AppSortTable attribute={"name"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <span>{lang.labels.originalFile}</span>
                      </th>
                      <th className="w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("size")}>
                          <span className="me-1">{lang.labels.size}</span>
                          <AppSortTable attribute={"size"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <span>{lang.labels.status}</span>
                      </th>
                      <th className="min-w-100px">
                        <span>{lang.labels.assignedTo}</span>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("dated_on")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"dated_on"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("uploaded_at")}>
                          <span className="me-1">{lang.labels.uploadedOn}</span>
                          <AppSortTable attribute={"uploaded_at"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px text-center"></th>
                    </tr>
                  </thead>
                  { pagination.total > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { documents.map (( (item, index) => { const itemId = item.id ?? item._id; return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <Link to={"/main/documents/view/" + itemId} className="fw-bolder text-dark text-hover-primary">{item.name ?? item.original_filename}</Link>
                          </td>
                          <td className="text-muted fs-7">{item.original_filename}</td>
                          <td className="text-end fs-7">{formatSize(item.size)}</td>
                          <td>
                            <span className={`badge badge-light-${colorStatusDocumentInbox(item.status)} fs-7`}>{statusDocumentInboxValue(lang, item.status)}</span>
                          </td>
                          <td>
                            {(item.assignments ?? []).length > 0
                              ? (item.assignments ?? []).map((a, aIdx) => {
                                  const label = assignmentEntityLabel(lang.labels, a.entity_type)
                                  const ref = assignmentDisplayReference(a)
                                  const url = assignmentEntityUrl(a.entity_type, a.entity_id)
                                  return (
                                    <span key={aIdx}>
                                      {aIdx > 0 && ', '}
                                      <span className="text-muted fs-7">{label}: </span>
                                      {url
                                        ? <Link to={url} className="fw-bold text-primary text-hover-dark">{ref}</Link>
                                        : <span className="fw-bold">{ref}</span>
                                      }
                                    </span>
                                  )
                                })
                              : <span className="text-muted">-</span>
                            }
                          </td>
                          <td className="fs-7">{item.dated_on ? dateFormat(lang.code, item.dated_on) : <span className="text-muted">-</span>}</td>
                          <td className="fs-7">{dateFormat(lang.code, item.uploaded_at)}</td>
                          <td className="text-end">
                            <DropdownActions
                              label={lang.labels.actions}
                              items={[
                                {
                                  key: 'view-pdf',
                                  label: lang.labels.viewDocument,
                                  icon: <i className="bi bi-eye fs-5"></i>,
                                  onClick: () => { openPdfViewer(itemId) },
                                },
                                {
                                  key: 'view-detail',
                                  label: lang.labels.document,
                                  icon: <i className="bi bi-pencil fs-5"></i>,
                                  onClick: () => navigate('/main/documents/view/' + itemId),
                                },
                                {
                                  key: 'copilot',
                                  label: lang.labels.copilotDiscussDocument,
                                  icon: <i className="bi bi-chat-dots fs-5"></i>,
                                  onClick: () => { openPdfViewer(itemId); openCopilot({ documentId: itemId, documentName: item.name ?? item.original_filename }) },
                                  hidden: !(isCopilotAllowed() && lifecycleView === 'active'),
                                },
                                {
                                  key: 'ignore',
                                  label: lang.labels.ignoreDocument,
                                  icon: <i className="bi bi-eye-slash fs-5"></i>,
                                  variant: 'warning',
                                  divider: true,
                                  onClick: () => submitIgnoreDocument(itemId),
                                  hidden: lifecycleView !== 'active',
                                },
                                {
                                  key: 'archive',
                                  label: lang.labels.archiveDocument,
                                  icon: <i className="bi bi-archive fs-5"></i>,
                                  onClick: () => submitArchiveDocument(itemId),
                                  hidden: lifecycleView !== 'active',
                                },
                                {
                                  key: 'unignore',
                                  label: lang.labels.unignoreDocument,
                                  icon: <i className="bi bi-arrow-counterclockwise fs-5"></i>,
                                  variant: 'success',
                                  divider: true,
                                  onClick: () => submitUnignoreDocument(itemId),
                                  hidden: lifecycleView !== 'ignored',
                                },
                                {
                                  key: 'unarchive',
                                  label: lang.labels.unarchiveDocument,
                                  icon: <i className="bi bi-arrow-counterclockwise fs-5"></i>,
                                  variant: 'success',
                                  divider: true,
                                  onClick: () => submitUnarchiveDocument(itemId),
                                  hidden: lifecycleView !== 'archived',
                                },
                                {
                                  key: 'delete',
                                  label: lang.labels.deleteDocument,
                                  icon: <i className="bi bi-trash fs-5"></i>,
                                  variant: 'danger',
                                  divider: true,
                                  onClick: () => submitDeleteDocument(itemId),
                                  hidden: !(lifecycleView === 'active' && item.status === 'new'),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalDocumentUpload companyForUser={companyForUser} onSuccess={handleUploadSuccess}></AppModalDocumentUpload>
    </div>
  )
}

export default DocumentListPage;
